import {
  ArrowDownTrayIcon,
  ClockIcon,
  CloudArrowUpIcon,
  DocumentTextIcon,
  InboxStackIcon,
  PhoneIcon,
} from '@heroicons/react/24/outline';
import { DollarSignIcon } from 'lucide-react';
import { toast } from 'sonner';

import {
  HipaaESignStatusStrip,
  useHipaaESignStatus,
} from '@/components/hipaa-esign';
import { Button } from '@/components/ui/catalyst/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip } from '@/components/ui/v3/Tooltip';
import { cn } from '@/lib/utils';

import { useRequestPage } from './contexts/RequestPageContext';
import type {
  PackageComponentSchema,
  RenderStatus,
  RequestPageHeaderProps,
  TabType,
} from './types';

// ============================================================================
// Types & Constants
// ============================================================================

interface TabItem {
  id: TabType;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
}

const TAB_ITEMS: TabItem[] = [
  { id: 'hipaa' as TabType, icon: InboxStackIcon, label: 'Package' },
  { id: 'records' as TabType, icon: DocumentTextIcon, label: 'Records' },
  { id: 'invoices' as TabType, icon: DollarSignIcon, label: 'Invoices' },
  { id: 'activity' as TabType, icon: ClockIcon, label: 'Activity' },
  { id: 'voice-ai-call' as TabType, icon: PhoneIcon, label: 'Voice AI' },
];

// ============================================================================
// Main Component
// ============================================================================

export const RequestPageHeader = ({
  activeTab,
  onTabChange,
  onClose,
}: RequestPageHeaderProps) => {
  const { request, provider, packageData, matterId, matter, clientName } =
    useRequestPage();

  // E-sign status (skip if no matterId to prevent 400 errors)
  const {
    status: eSignStatus,
    hasRequest: hasESignRequest,
    refreshStatus,
    isRefreshing,
    sendReminder,
    isSendingReminder,
    voidRequest,
    isVoiding,
    canVoid,
  } = useHipaaESignStatus({
    matterId,
    skip: !matterId,
    refreshOnMount: true,
  });

  // Get firm name from matter
  const firmName = matter?.firm?.name;

  if (!request || !provider) return null;

  // Check if package has HIPAA auth template
  const hasHipaaAuthTemplate = packageData?.components?.some(
    (component: PackageComponentSchema) =>
      component.template?.category === 'HIPAA_AUTHORIZATION',
  );

  return (
    <div className="border-b border-zinc-200 bg-white">
      {/* Top row: Breadcrumb + Status + Actions */}
      <div className="px-6 pt-4 pb-3">
        <div className="flex items-center justify-between">
          {/* Left: Provider context + status */}
          <div className="flex items-center gap-4">
            <HeaderBreadcrumb />
            {activeTab === 'hipaa' && packageData && (
              <StatusBadge status={packageData.status} />
            )}
          </div>

          {/* Right: Actions */}
          <div className="flex items-center gap-2">
            {activeTab === 'hipaa' && <PackageModeActions />}
            {onClose && (
              <Button onClick={onClose} plain>
                Close
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* E-Sign Status Strip (when request exists and HIPAA auth template is in package) */}
      {hasESignRequest && hasHipaaAuthTemplate && eSignStatus && (
        <div className="px-6 pb-3">
          <HipaaESignStatusStrip
            status={eSignStatus}
            onRefresh={refreshStatus}
            onSendReminder={sendReminder}
            onVoid={voidRequest}
            isRefreshing={isRefreshing}
            isSendingReminder={isSendingReminder}
            isVoiding={isVoiding}
            canVoid={canVoid}
            clientName={clientName}
            firmName={firmName}
          />
        </div>
      )}

      {/* Bottom row: Tab navigation */}
      <TabNavigation activeTab={activeTab} onTabChange={onTabChange} />
    </div>
  );
};

// ============================================================================
// Sub-components
// ============================================================================

/** Breadcrumb component showing provider context */
const HeaderBreadcrumb = () => {
  const { provider } = useRequestPage();

  if (!provider) return null;

  return (
    <div className="flex items-center gap-2.5 text-base">
      <span className="font-semibold text-zinc-900">{provider.name}</span>
    </div>
  );
};

/** Tab navigation component */
const TabNavigation = ({
  activeTab,
  onTabChange,
}: {
  activeTab: TabType;
  onTabChange: (tab: TabType) => void;
}) => (
  <div className="px-6">
    <nav className="flex gap-1 -mb-px">
      {TAB_ITEMS.map((tab) => (
        <button
          key={tab.id}
          type="button"
          onClick={() => onTabChange(tab.id)}
          className={cn(
            'flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors',
            activeTab === tab.id
              ? 'border-zinc-900 text-zinc-900'
              : 'border-transparent text-zinc-500 hover:text-zinc-700 hover:border-zinc-300',
          )}
        >
          <tab.icon className="h-4 w-4" />
          {tab.label}
        </button>
      ))}
    </nav>
  </div>
);

/** Unified package action buttons */
const PackageModeActions = () => {
  const {
    provider,
    packageData,
    packageMode,
    setPackageMode,
    validationStatus,
    isRendering,
    isPublishing,
    handleRender,
    handlePublish,
    openModal,
    hasMissingSignature,
    canEditPackage,
  } = useRequestPage();

  if (!packageData) return null;

  const providerName = provider?.name || 'Provider';
  const packageStatus = packageData.status;
  const isPublished = packageStatus === 'published';
  const canPublish = packageStatus === 'ready';
  const hasRenderedPackage = !!packageData.rendered_pdf_url;
  const hasDownloadablePackage =
    hasRenderedPackage || !!packageData.download_url;
  const hasComponents = (validationStatus?.componentCount ?? 0) > 0;

  // Check if package is generating
  const isPackageGenerating = packageStatus === 'generating';
  const hasGeneratingComponents =
    packageData.components?.some(
      (c: PackageComponentSchema) => c.status === 'generating',
    ) || false;
  const isGenerating = isPackageGenerating || hasGeneratingComponents;

  // Check for HIPAA auth template and signature requirements
  const hasHipaaAuthTemplate = packageData.components?.some(
    (component: PackageComponentSchema) =>
      component.template?.category === 'HIPAA_AUTHORIZATION',
  );
  const shouldBlockForSignature = hasHipaaAuthTemplate && hasMissingSignature;
  const { hasMissingFields } = validationStatus;

  // Build filename for downloads
  const buildFilename = (extension: 'pdf' | 'docx') => {
    const parts = ['HIPAA_Package'];
    if (providerName) {
      parts.push(providerName.replace(/[^a-zA-Z0-9]/g, '_'));
    }
    parts.push(new Date().toISOString().split('T')[0]);
    return `${parts.join('_')}.${extension}`;
  };

  // Handle file download
  const handleDownload = async (
    url: string | null | undefined,
    extension: 'pdf' | 'docx',
  ) => {
    if (!url) {
      toast.error('Download not available');
      return;
    }

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error('Download failed');

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = buildFilename(extension);
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch {
      // Fallback to opening in new tab
      window.open(url, '_blank');
    }
  };

  // Count total issues
  const issueCount =
    (hasMissingFields ? validationStatus.missingFieldsCount : 0) +
    (shouldBlockForSignature ? 1 : 0);
  const hasIssues = issueCount > 0 || validationStatus.hasValidationIssues;

  // Build tooltip for disabled approve button
  const getApproveDisabledReason = () => {
    const reasons: string[] = [];
    if (hasMissingFields) {
      reasons.push(
        `${validationStatus.missingFieldsCount} missing field${validationStatus.missingFieldsCount !== 1 ? 's' : ''}`,
      );
    }
    if (shouldBlockForSignature) {
      reasons.push('missing signature');
    }
    if (reasons.length === 0) return null;
    return `Cannot approve: ${reasons.join(', ')}`;
  };

  const approveDisabledReason = getApproveDisabledReason();
  const isApproveDisabled =
    isPublishing || hasMissingFields || shouldBlockForSignature;

  return (
    <>
      {/* Edit Mode Actions - Order: Regenerate → Preview → Upload → Status */}
      {packageMode === 'edit' && (
        <>
          {/* Regenerate */}
          {hasComponents && (
            <Button
              onClick={handleRender}
              disabled={isRendering || isGenerating}
              outline
            >
              {isRendering ? 'Starting...' : 'Regenerate'}
            </Button>
          )}

          {/* Preview */}
          {hasDownloadablePackage && packageStatus !== 'error' && (
            <Button outline onClick={() => setPackageMode('view')}>
              Preview
            </Button>
          )}

          {/* Upload */}
          {!isPublished && (
            <Button outline onClick={() => openModal('upload')}>
              <CloudArrowUpIcon />
            </Button>
          )}

          {/* Status Indicator */}
          {isGenerating ? (
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-blue-50 text-blue-700">
              <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-blue-200 border-t-blue-600" />
              <span className="text-xs font-medium">Generating...</span>
            </div>
          ) : hasIssues ? (
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-amber-50 text-amber-700">
              <svg
                className="h-3.5 w-3.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <span className="text-xs font-medium">
                {shouldBlockForSignature && hasMissingFields
                  ? `Signature + ${validationStatus.missingFieldsCount} field${validationStatus.missingFieldsCount !== 1 ? 's' : ''}`
                  : shouldBlockForSignature
                    ? 'Missing signature'
                    : hasMissingFields
                      ? `${validationStatus.missingFieldsCount} missing field${validationStatus.missingFieldsCount !== 1 ? 's' : ''}`
                      : 'Issues to resolve'}
              </span>
            </div>
          ) : packageStatus === 'ready' ? (
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-green-50 text-green-700">
              <svg
                className="h-3.5 w-3.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
              <span className="text-xs font-medium">Ready</span>
            </div>
          ) : null}

          {/* Approve Button */}
          {canPublish &&
            !isPublished &&
            (isApproveDisabled && approveDisabledReason ? (
              <Tooltip content={approveDisabledReason}>
                <span className="inline-flex">
                  <Button onClick={handlePublish} disabled color="indigo">
                    Approve
                  </Button>
                </span>
              </Tooltip>
            ) : (
              <Button
                onClick={handlePublish}
                disabled={isPublishing}
                color="indigo"
              >
                {isPublishing ? 'Approving...' : 'Approve'}
              </Button>
            ))}
        </>
      )}

      {/* View Mode Actions */}
      {packageMode === 'view' && (
        <>
          {canPublish &&
            !isPublished &&
            (isApproveDisabled && approveDisabledReason ? (
              <Tooltip content={approveDisabledReason}>
                <span className="inline-flex">
                  <Button onClick={handlePublish} disabled color="indigo">
                    Approve
                  </Button>
                </span>
              </Tooltip>
            ) : (
              <Button
                onClick={handlePublish}
                disabled={isPublishing}
                color="indigo"
              >
                {isPublishing ? 'Approving...' : 'Approve'}
              </Button>
            ))}

          {!canEditPackage ? (
            <Tooltip content="Request already submitted. HIPAA package edits are locked.">
              <span className="inline-flex">
                <Button outline disabled>
                  Edit
                </Button>
              </span>
            </Tooltip>
          ) : (
            <Button outline onClick={() => setPackageMode('edit')}>
              Edit
            </Button>
          )}
        </>
      )}

      {/* Download Dropdown */}
      {hasDownloadablePackage &&
        !isGenerating &&
        (packageStatus === 'ready' || packageStatus === 'published') && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button outline>
                <ArrowDownTrayIcon />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() =>
                  handleDownload(
                    packageData.rendered_pdf_url || packageData.download_url,
                    'pdf',
                  )
                }
              >
                <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                Download PDF
              </DropdownMenuItem>
              {packageData.rendered_docx_url && (
                <DropdownMenuItem
                  onClick={() =>
                    handleDownload(packageData.rendered_docx_url, 'docx')
                  }
                >
                  <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                  Download Word
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}

      {/* Divider */}
      <div className="h-6 w-px bg-gray-300 mx-1" />
    </>
  );
};

/** Package status badge */
const StatusBadge = ({ status }: { status: RenderStatus }) => {
  const getStatusColor = () => {
    switch (status) {
      case 'published':
        return 'bg-green-100 text-green-800';
      case 'ready':
        return 'bg-blue-100 text-blue-800';
      case 'generating':
        return 'bg-yellow-100 text-yellow-800';
      case 'missing_fields':
        return 'bg-orange-100 text-orange-800';
      case 'error':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = () => {
    switch (status) {
      case 'published':
        return 'Approved';
      case 'ready':
        return 'Ready';
      case 'generating':
        return 'Generating...';
      case 'missing_fields':
        return 'Missing Fields';
      case 'error':
        return 'Error';
      case 'draft':
        return 'Draft';
      default:
        return status;
    }
  };

  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor()}`}
    >
      {getStatusLabel()}
    </span>
  );
};
